import Box from '@mui/material/Box/Box';
import { Layout } from 'components/Layout';
import { MetaHead } from 'components/MetaHead';
import { PageMeta, policyMeta, resourcesMeta } from 'constants/pages';
import { ScrollContext } from 'context/ScrollContext';
import { FC, memo, useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArticleSections,
  IHeaderSection,
  IListSection,
  ITextWithImageSection,
  LeftMenuItem,
} from 'types/articles';
import { getLeftMenuItems } from 'utils/getLeftMenuItems';
import {
  LeftMenuTop,
  LeftMenuWrapper,
  SubPageSubscribeButton,
  TermsAndConditionsWrapper,
  WaypointsButton,
} from './Resource.styles';
import { colors } from 'styles/colors';
import List from '@mui/material/List/List';
import ListItemButton from '@mui/material/ListItemButton/ListItemButton';
import Typography from '@mui/material/Typography/Typography';
import Collapse from '@mui/material/Collapse/Collapse';
import ListItemText from '@mui/material/ListItemText/ListItemText';
import ListItem from '@mui/material/ListItem/ListItem';
import { HeaderSection } from 'components/Articles/HeaderSection/HeaderSection';
import { ListSection } from 'components/Articles/ListSection/ListSection';
import { TextWithImageSection } from 'components/Articles/TextWithImageSection/TextWithImageSection';
import { SubscribeModal } from 'components/SubscribeModal';
import { ContactModal } from 'components/Header/ContactModal';
import { ResourceAccessModal } from 'modules/Insights/DownloadableResources/ResourceAccessModal';
import { ResearchQuestionModal } from 'modules/Insights/DownloadableResources/ResearchQuestionModal';
import { PartialResources } from 'modules/Insights/PartialResources';
import { useMediaQuery } from '@mui/material';
import { theme } from 'styles';

const HEADER_OFFSET = 120;

interface IResourceProps {
  pageMeta: PageMeta;
  articleData: ArticleSections[];
}

export const Resource: FC<IResourceProps> = memo(({ articleData, pageMeta }) => {
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const navigate = useNavigate();
  const isScrolled = useContext(ScrollContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isResearchQuestionModalOpen, setIsResearchQuestionModalOpen] = useState(false);
  const [resourceAccessConfig, setResourceAccessConfig] = useState<{
    url: string;
    title: string;
    pageTag: string;
  } | null>(null);

  const leftMenuItems = getLeftMenuItems(articleData);

  const flatIds = useMemo(() => {
    const ids: string[] = [];
    leftMenuItems.forEach((item) => {
      ids.push(item.id);
      if (item.submenu?.length) {
        item.submenu.forEach((_, i) => ids.push(`${item.id}-sub${i + 1}`));
      }
    });
    return ids;
  }, [leftMenuItems]);

  const getAbsTop = (el: HTMLElement | null) =>
    el ? el.getBoundingClientRect().top + window.scrollY : Number.POSITIVE_INFINITY;

  useEffect(() => {
    const computeActiveId = () => {
      const refY = window.scrollY + HEADER_OFFSET + 1;

      let current = flatIds[0] ?? null;

      for (let i = 0; i < flatIds.length; i++) {
        const id = flatIds[i];
        const el = document.getElementById(id);
        if (!el) continue;

        const top = getAbsTop(el);
        if (top <= refY) {
          current = id;
        } else {
          break;
        }
      }

      return current;
    };

    const applyActive = () => {
      const id = computeActiveId();
      if (!id) return;

      setActiveSectionId((prev) => (prev === id ? prev : id));

      const parentId = id.includes('-sub') ? id.split('-sub')[0] : id;
      const parent = leftMenuItems.find((i) => i.id === parentId);
      if (parent?.submenu?.length) {
        setExpandedItemId(parentId);
      } else {
        setExpandedItemId(null);
      }
    };

    applyActive();

    const onScroll = () => {
      applyActive();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', applyActive);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', applyActive);
    };
  }, [flatIds, leftMenuItems]);

  const handleClick = (item: LeftMenuItem) => {
    if (item.submenu) {
      setExpandedItemId((prev) => (prev === item.id ? null : item.id));
    } else {
      setExpandedItemId(null);
    }

    const section = document.getElementById(item.id);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
  };

  const waypointButtonClick = () => {
    setIsNavOpen((prev) => !prev);
  };

  const handleSubClick = (parentId: string, index: number) => {
    if (parentId) {
      setExpandedItemId((prev) => (prev === parentId ? null : parentId));
    } else {
      setExpandedItemId(null);
    }

    const subId = `${parentId}-sub${index + 1}`;
    const subSection = document.getElementById(subId);
    if (subSection) {
      subSection.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
  };

  const toggleSubscribeModal = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const toggleContactModal = useCallback(() => {
    setIsContactModalOpen((prev) => !prev);
  }, []);

  const toggleResearchQuestionModal = useCallback(() => {
    setIsResearchQuestionModalOpen((prev) => !prev);
  }, []);

  const openResourceAccessModal = useCallback(
    (config: { url: string; title: string; pageTag: string }) => {
      setResourceAccessConfig(config);
    },
    [],
  );

  const closeResourceAccessModal = useCallback(() => {
    setResourceAccessConfig(null);
  }, []);

  // Expose modal functions to window for use in article text sections
  // This is needed because article text is static data (ReactNode) that cannot receive callbacks via props
  useEffect(() => {
    const win = window as typeof window & {
      openAskQuestionModal?: () => void;
      openSubscribeModal?: () => void;
      openContactModal?: () => void;
      openResourceAccessModal?: (config: {
        url: string;
        title: string;
        pageTag: string;
      }) => void;
    };
    win.openAskQuestionModal = toggleResearchQuestionModal;
    win.openSubscribeModal = toggleSubscribeModal;
    win.openContactModal = toggleContactModal;
    win.openResourceAccessModal = openResourceAccessModal;

    return () => {
      delete win.openAskQuestionModal;
      delete win.openSubscribeModal;
      delete win.openContactModal;
      delete win.openResourceAccessModal;
    };
  }, [
    toggleSubscribeModal,
    toggleContactModal,
    toggleResearchQuestionModal,
    openResourceAccessModal,
  ]);

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;

    if (el === null) {
      return;
    }

    const handleWheel = (e: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = el;

      const isScrollable = scrollHeight > clientHeight;
      if (!isScrollable) return;

      const shouldBlock =
        (scrollTop === 0 && e.deltaY < 0) ||
        (scrollTop + clientHeight >= scrollHeight - 1 && e.deltaY > 0);

      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener('wheel', handleWheel, { passive: false });

    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleTransitionEnd = (e: TransitionEvent) => {
      if ((e.propertyName === 'left' || e.propertyName === 'transform') && activeSectionId) {
        const section = document.getElementById(activeSectionId);
        section?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
      }
    };

    el.addEventListener('transitionend', handleTransitionEnd);
    return () => el.removeEventListener('transitionend', handleTransitionEnd);
  }, [activeSectionId]);

  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));

  return (
    <Layout>
      <MetaHead pageMeta={pageMeta} />
      <WaypointsButton onClick={waypointButtonClick} isOpen={isNavOpen}></WaypointsButton>
      <Box display="flex">
        <Box
          padding={{
            xxs: '20px 0',
            sm: '34px 0',
            md: '20px',
            lg: '0 0 0 40px',
          }}
          marginTop={{
            lg: '146px',
            xxs: 0,
          }}
          marginRight="42px"
          ref={ref}
          display={{
            sm: 'block',
          }}
          position={{
            xxs: 'fixed',
            md: 'static',
          }}
          bgcolor={{
            xxs: colors.white,
          }}
          top={{
            xxs: '128px',
            sm: '82px',
            md: 0,
          }}
          left={{
            xxs: isNavOpen ? '16px' : '-300px',
            sm: isNavOpen ? '24px' : '-260px',
            md: 0,
          }}
          zIndex="2"
          sx={{
            transition: 'left 0.3s ease, top 0.3s ease',
          }}
        >
          <LeftMenuWrapper>
            {!isScrolled && (
              <LeftMenuTop>
                <SubPageSubscribeButton
                  color="secondary"
                  variant="contained"
                  onClick={toggleSubscribeModal}
                >
                  Subscribe
                </SubPageSubscribeButton>

                <TermsAndConditionsWrapper>
                  By clicking Subscribe you&apos;re confirming that you agree with our{' '}
                  <a
                    href={policyMeta.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: colors.mainDark,
                    }}
                  >
                    Terms and Conditions.
                  </a>
                </TermsAndConditionsWrapper>
              </LeftMenuTop>
            )}

            {leftMenuItems.length > 2 && (
              <Box
                marginTop={{
                  xxs: 0,
                  md: '42px',
                }}
                paddingBottom="16px"
                overflow="auto"
                maxHeight={
                  isXs || isSm
                    ? 'calc(85vh - 128px)'
                    : isScrolled
                    ? 'calc(100vh - 250px)'
                    : 'calc(100vh - 350px)'
                }
                sx={{
                  scrollbarColor: '#E3E3E3 transparent',
                  scrollbarWidth: 'thin',
                }}
              >
                <List disablePadding>
                  {leftMenuItems.map((item) => {
                    const isActive = item.id === activeSectionId;
                    const isExpanded = expandedItemId === item.id;

                    return (
                      <Box key={item.id} display="flex" flexDirection="column" height="100%">
                        <Box display="flex" alignItems="stretch" height="100%">
                          <Box
                            width="8px"
                            height="auto"
                            sx={{
                              borderRadius: isActive ? '10px' : 0,
                              backgroundColor: isActive
                                ? 'rgba(52, 142, 233, 1)'
                                : 'rgba(239, 240, 242, 1)',
                            }}
                          />

                          <ListItemButton onClick={() => handleClick(item)}>
                            <Typography
                              fontWeight={700}
                              textAlign="left"
                              fontSize="14px"
                              maxWidth={isSm || isXs ? '230px' : '200px'}
                              sx={{
                                color: 'rgba(139, 142, 147, 1)',
                              }}
                            >
                              {item.label}
                            </Typography>
                          </ListItemButton>
                        </Box>

                        {item.submenu && (
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                              {item.submenu.map((sub, idx) => {
                                const subId = `${item.id}-sub${idx + 1}`;
                                const isSubActive = activeSectionId === subId;

                                return (
                                  <Box display="flex" key={subId}>
                                    <Box
                                      width="8px"
                                      sx={{
                                        backgroundColor: isSubActive
                                          ? 'rgba(52, 142, 233, 1)'
                                          : 'rgba(239, 240, 242, 1)',
                                        borderRadius: isSubActive ? '10px' : 0,
                                      }}
                                    />

                                    <ListItem
                                      disablePadding
                                      onClick={() => handleSubClick(item.id, idx)}
                                      sx={{
                                        paddingLeft: '16px',
                                      }}
                                    >
                                      <ListItemButton
                                        sx={{
                                          maxWidth: 200,
                                          color: 'rgba(139, 142, 147, 1)',
                                        }}
                                      >
                                        <ListItemText
                                          primary={`● ${sub}`}
                                          primaryTypographyProps={{
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            lineHeight: '120%',
                                          }}
                                        />
                                      </ListItemButton>
                                    </ListItem>
                                  </Box>
                                );
                              })}
                            </List>
                          </Collapse>
                        )}
                      </Box>
                    );
                  })}
                </List>
              </Box>
            )}
          </LeftMenuWrapper>
        </Box>

        <Box
          display="flex"
          width={{
            lg: 'calc(100vw - 684px)',
            xxs: '100%',
          }}
          justifyContent={{
            lg: 'center',
            xxs: 'inherit',
          }}
        >
          <Box
            marginTop={{
              lg: '162px',
              md: '56px',
              sm: '34px',
              xxs: '56px',
            }}
            marginLeft={{
              sm: isNavOpen ? '290px' : 0,
              md: 0,
            }}
            sx={{
              transition: 'margin-left 0.3s ease, top 0.3s ease',
            }}
            paddingRight={{
              xxs: '0',
              lg: '40px',
            }}
            padding={{
              xxs: '0 16px',
              sm: '0 48px',
              lg: 0,
            }}
          >
            {articleData.map((section, sectionIndex) => (
              <Box
                key={section.title}
                id={section.title}
                ref={(el) => (sectionRefs.current[sectionIndex] = el as HTMLElement | null)}
                sx={{
                  scrollMarginTop: '120px',
                }}
              >
                <Box
                  marginBottom={{
                    sm: '48px',
                    xxs: '48px',
                  }}
                >
                  {section.type === 'header' && <HeaderSection {...(section as IHeaderSection)} />}

                  {section.type === 'list' && <ListSection {...(section as IListSection)} />}

                  {section.type === 'common' && (
                    <TextWithImageSection {...(section as ITextWithImageSection)} />
                  )}

                  {((section as ITextWithImageSection)?.submenu || [])?.map(
                    (subSection, subIdx) => {
                      const subId = `${section.title}-sub${subIdx + 1}`;
                      return (
                        <Box
                          key={subId}
                          id={subId}
                          marginTop={{
                            sm: '48px',
                            xxs: '24px',
                          }}
                          ref={(el) =>
                            (sectionRefs.current[sectionIndex] = el as HTMLElement | null)
                          }
                          sx={{
                            scrollMarginTop: '120px',
                          }}
                        >
                          <TextWithImageSection
                            {...(subSection as ITextWithImageSection)}
                            sectionType="secondary"
                          />
                        </Box>
                      );
                    },
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        width="100%"
        display="flex"
        justifyContent="center"
        marginTop={{
          lg: '130px',
          xxs: '56px',
        }}
      >
        <Box
          width="100%"
          maxWidth="1440px"
          sx={{
            borderTop: `1px solid ${colors.secondaryButtonDisabled}`,
          }}
        >
          <PartialResources
            scrollToSection={() => navigate(`${resourcesMeta.url}#all-resources`)}
          />
        </Box>
      </Box>

      <SubscribeModal isOpen={isOpen} onClose={toggleSubscribeModal} />
      <ContactModal isOpen={isContactModalOpen} onClose={toggleContactModal} />
      <ResourceAccessModal
        isOpen={resourceAccessConfig !== null}
        onClose={closeResourceAccessModal}
        resourceTitle={resourceAccessConfig?.title ?? ''}
        resourceUrl={resourceAccessConfig?.url ?? ''}
        pageTag={resourceAccessConfig?.pageTag ?? ''}
      />
      <ResearchQuestionModal
        isOpen={isResearchQuestionModalOpen}
        onClose={toggleResearchQuestionModal}
        pageTag={pageMeta.url}
      />
    </Layout>
  );
});
